
import React, { useState, useEffect } from "react";
import { formatAffirmationOptions, rephraseToPresentTense } from "../utils/manifestationSteps";
import ChatMessageList from "./ChatMessageList";
import ChatInput from "./ChatInput";
import { useManifestation } from "../hooks/useManifestation";
import { useToast } from "../hooks/use-toast";
import { Button } from "./ui/button";
import { Trash2, Sparkles, ImageOff } from "lucide-react";
import { rephraseGoalWithAI } from "../utils/rephraseGoalAI";
import { getContextualResponse } from "../utils/contextualResponseAI";
import ManifestationCompleteDialog from "./ManifestationCompleteDialog";
import BackdropLayer from "./chatbot/BackdropLayer";
import BackdropBuilder from "./chatbot/BackdropBuilder";


const ChatBot: React.FC<{ isIdle?: boolean }> = ({ isIdle = false }) => {
  // pass isIdle down for hiding messages
  const {
    messages,
    currentStep,
    manifestationData,
    addBotMessage,
    addUserMessage,
    processUserInput,
    resetManifestation,
    resetAllAffirmations,
    selectedAffirmationIndex,
    setActiveBackdrop,
  } = useManifestation();
  
  const [isTyping, setIsTyping] = useState(false);
  const [messagesHidden, setMessagesHidden] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showBackdropBuilder, setShowBackdropBuilder] = useState(false);
  const [pendingAnimationVideo, setPendingAnimationVideo] = useState<string | null>(null);
  const { toast } = useToast();

  const activeBackdrop = manifestationData.activeBackdrop;
  const hasActiveAffirmation = !!manifestationData.presentTenseGoal?.trim();
  // Backdrop toggle is always available at the start of the DM the Universe process.
  const canOfferBackdrop = true;


  // No longer need respond step auto-send or waiting state

  const handleSendMessage = (inputValue: string, isAutomatic = false) => {
    if (inputValue.trim() === "") return;

    if (inputValue.trim().toLowerCase() === "/start") {
      addUserMessage(inputValue);
      simulateTypingAndAddBotMessage("Starting a new manifestation process...");
      resetManifestation();
      return;
    }

    if (inputValue.trim().toLowerCase() === "reset") {
      addUserMessage(inputValue);
      simulateTypingAndAddBotMessage("All saved affirmations have been cleared. Let's start fresh! 🌟");
      resetAllAffirmations();
      return;
    }

    if (!isAutomatic) {
      addUserMessage(inputValue);
    }

    try {

      const previousStep = currentStep;
      const { message, nextStep, updatedData, isAffirmationSelection } = processUserInput(inputValue);

      // If the session is completed, show the bot's final message then open the dialog
      if (nextStep === "completed") {
        simulateTypingAndAddBotMessage(message, undefined, undefined, nextStep);
        setTimeout(() => setShowCompleteDialog(true), 1200);
        return;
      }

      if (isAffirmationSelection) {
        const selectedText = updatedData.presentTenseGoal;
        simulateTypingAndAddBotMessage(`You selected: "${selectedText}"\n\nHow many manifestation cycles would you like to complete? (Choose a number between 1-12)\n\nℹ️ *A cycle is one round of writing, feeling, and visualizing your affirmation.*`, undefined, undefined, "goal-setting");
        return;
      }

      if (inputValue.toLowerCase().includes("delete") && 
          updatedData.savedAffirmations.length !== manifestationData.savedAffirmations.length) {
        simulateTypingAndAddBotMessage(message, undefined, undefined, nextStep);
        toast({
          title: "Affirmation deleted",
          description: "Your affirmation has been removed from saved list",
        });
      } else {
        // Use AI contextual response for steps where user input matters
        // NOTE: `respond` and `reality-check` are intentionally excluded — those
        // steps now emit structured deterministic messages (tips + auto-advance
        // into the next cycle or core-feeling) that the AI shouldn't overwrite.
        const contextualSteps = ['affirmation-choice', 'goal-confirmation', 'goal-setting', 'cycle-setup', 'present-tense', 'feeling-state', 'summary', 'core-feeling'];

        // Don't use AI for structured transitions (next-cycle prompts, save prompt, completion summary).
        const isNextCycleTransition = message.includes('Type out your affirmation')
          || message.includes('would you like to save this affirmation');
        
        if (contextualSteps.includes(previousStep) && !isAutomatic && !isNextCycleTransition) {
          // Collect recent bot/user messages for conversation history so AI avoids repetition
          // and can resolve back-references like "as I said before".
          const recentBotMessages = messages
            .filter(m => !m.isUser)
            .slice(-8)
            .map(m => m.content);
          const recentUserMessages = messages
            .filter(m => m.isUser)
            .slice(-4)
            .map(m => m.content);

          // For the visualization step, pass the deterministic angle so the AI
          // doesn't pick its own and repeat across cycles.
          let forcedAngle: string | undefined;
          if (previousStep === 'present-tense') {
            const used = updatedData.usedVisualizationAngles || [];
            forcedAngle = used[used.length - 1];
          }

          simulateTypingAndAddBotMessage(message, updatedData.goal || undefined, {
            step: previousStep,
            userMessage: inputValue,
            manifestationData: updatedData,
            previousBotMessages: recentBotMessages,
            previousUserMessages: recentUserMessages,
            forcedAngle,
          }, nextStep);
        } else {
          simulateTypingAndAddBotMessage(message, updatedData.goal || undefined, undefined, nextStep);
        }
      }
    } catch (error) {
      console.error("Error processing user input:", error);
      toast({
        title: "Something went wrong",
        description: "There was an error processing your input. Please try again.",
        variant: "destructive"
      });
    }
  };

  const simulateTypingAndAddBotMessage = async (
    content: string, 
    goal?: string,
    contextualParams?: { step: string; userMessage: string; manifestationData: any; previousBotMessages?: string[]; previousUserMessages?: string[]; forcedAngle?: string },
    messageStep?: string
  ) => {
    setIsTyping(true);
    
    let finalContent = content;
    
    // If the message has a pending AI rephrase, fetch it
    if (finalContent.includes("{AI_REPHRASE_PENDING}") && goal) {
      try {
        const aiRephrased = await rephraseGoalWithAI(goal);
        if (aiRephrased) {
          finalContent = finalContent.replace("{AI_REPHRASE_PENDING}", aiRephrased);
        } else {
          finalContent = finalContent.replace("{AI_REPHRASE_PENDING}", rephraseToPresentTense(goal));
        }
      } catch {
        finalContent = finalContent.replace("{AI_REPHRASE_PENDING}", rephraseToPresentTense(goal));
      }
    }
    
    // Try to get a contextual AI response
    if (contextualParams) {
      try {
        const aiResponse = await getContextualResponse({
          step: contextualParams.step as any,
          userMessage: contextualParams.userMessage,
          manifestationData: contextualParams.manifestationData,
          fallbackMessage: finalContent,
          previousBotMessages: contextualParams.previousBotMessages,
          previousUserMessages: contextualParams.previousUserMessages,
          forcedAngle: contextualParams.forcedAngle,
        });
        if (aiResponse && aiResponse.trim().length > 0) {
          // Preserve cycle indicator prefix (e.g. "**Cycle 2 of 5**") if present in original
          const cycleMatch = finalContent.match(/^(\*\*Cycle \d+ of \d+\*\*\n\n)/);
          let newContent = cycleMatch ? cycleMatch[1] + aiResponse : aiResponse;

          // Affirmation fidelity: if the AI inlines the affirmation as prose,
          // strip those inline mentions and append a clean bold+quoted line.
          // We no longer proactively echo the affirmation when the AI didn't
          // mention it — the affirmation is already visible on-screen, and
          // re-quoting it every turn creates robotic clutter (per user review).
          const ptg = contextualParams.manifestationData?.presentTenseGoal;
          if (ptg && contextualParams.step !== "cycle-setup") {
            const escaped = ptg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const wrappedForm = `**"${ptg}"**`;
            const inlineMatcher = new RegExp(`(?<!\\*\\*")${escaped}(?!"\\*\\*)`, "g");
            if (inlineMatcher.test(newContent)) {
              newContent = newContent.replace(inlineMatcher, "this").replace(/\s+/g, " ").trim();
              if (!newContent.includes(wrappedForm)) {
                newContent += `\n\n${wrappedForm}`;
              }
            }
          }

          finalContent = newContent;
        }
      } catch {
        // Keep fallback message
      }
    }
    
    const baseDelay = Math.min(finalContent.length * 2, 400);
    const randomFactor = Math.random() * 100 + 50;
    
    setTimeout(() => {
      addBotMessage(finalContent, messageStep as any);
      setIsTyping(false);
    }, baseDelay + randomFactor);
  };

  return (
    <div className="flex flex-col h-full max-w-4xl w-full mx-auto bg-transparent overflow-hidden">
      {activeBackdrop && (
        <BackdropLayer
          imageUrl={activeBackdrop.imageUrl}
          videoUrl={pendingAnimationVideo ?? activeBackdrop.videoUrl}
          isAnimating={!activeBackdrop.videoUrl && !pendingAnimationVideo}
        />
      )}

      <div className="pt-4 pb-2 px-4 flex justify-end items-center gap-2">
        {canOfferBackdrop && !activeBackdrop && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs flex items-center gap-1"
            onClick={() => setShowBackdropBuilder(true)}
          >
            <Sparkles size={14} />
            Generate Vision Backdrop
          </Button>
        )}
        {activeBackdrop && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="text-xs flex items-center gap-1"
              onClick={() => setShowBackdropBuilder(true)}
            >
              <Sparkles size={14} />
              Change backdrop
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs flex items-center gap-1"
              onClick={() => {
                setActiveBackdrop(undefined);
                setPendingAnimationVideo(null);
              }}
            >
              <ImageOff size={14} />
              Remove
            </Button>
          </>
        )}
        <Button 
          variant="outline" 
          size="sm"
          className="text-xs flex items-center gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={resetAllAffirmations}
        >
          <Trash2 size={14} />
          Reset All Affirmations
        </Button>
      </div>
      
      <div
        className="flex flex-col flex-1 min-h-0"
      >
        <ChatMessageList 
          messages={messages} 
          isTyping={isTyping}
          isIdle={isIdle || messagesHidden}
        />
        
        <ChatInput 
          onSendMessage={handleSendMessage} 
          isTyping={isTyping}
          messagesHidden={messagesHidden}
          onToggleMessages={() => setMessagesHidden(h => !h)}
        />
      </div>

      <ManifestationCompleteDialog
        open={showCompleteDialog}
        onClose={() => setShowCompleteDialog(false)}
      />

      <BackdropBuilder
        open={showBackdropBuilder}
        onClose={() => setShowBackdropBuilder(false)}
        affirmation={manifestationData.presentTenseGoal || "my goal"}
        onComplete={(bd) => {
          setActiveBackdrop(bd);
          setPendingAnimationVideo(null);
        }}
        onAnimationDone={(videoUrl) => {
          if (videoUrl) {
            setPendingAnimationVideo(videoUrl);
            // Persist on the active backdrop + saved affirmation
            if (manifestationData.activeBackdrop) {
              setActiveBackdrop({ ...manifestationData.activeBackdrop, videoUrl });
            }
          }
        }}
      />

    </div>
  );
};


export default ChatBot;
